/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { type SkuAvailabilty } from '@/types/availability';

type AddToCartInfoParams = {
  skuDetailsData?: SkuAvailabilty[] | null;
  id?: string;
  productId?: string | number;
  thresholdHeaderText?: string;
  acesEnabled: boolean;
};

/**
 * Retrieves add to cart information for a specific SKU based on availability data and configuration.
 *
 * @param params - Configuration object containing SKU details and settings
 * @param params.skuDetailsData - Array of SKU availability data to search through
 * @param params.id - SKU ID to find in the availability data
 * @param params.productId - Product ID to use as fallback if not found in SKU details
 * @param params.thresholdHeaderText - Text that determines fulfillment method selection
 * @param params.acesEnabled - Flag to determine which parts information structure to use
 * @returns Object containing add to cart information including SKU ID, product ID, fulfillment method, and parts info, or empty object if SKU not found
 */
export const getAddToCartInfo = (params: AddToCartInfoParams) => {
  const { skuDetailsData, id, thresholdHeaderText, productId, acesEnabled } = params;
  const skuDetails = skuDetailsData?.find((data: SkuAvailabilty) => {
    return data.skuId === id;
  });

  const bopusQuantity = skuDetails?.availabilityInfoVO.bopusQuantity;
  const sthQuantity = skuDetails?.availabilityInfoVO.sthQuantity;
  const nddQuantity = skuDetails?.availabilityInfoVO.nddQuantity;

  const fulFillmentId =
    !!bopusQuantity && bopusQuantity > 0
      ? FULFILLMENT_METHODS.STOREORDER
      : !!sthQuantity && sthQuantity > 0
      ? FULFILLMENT_METHODS.ONLINEORDER
      : null;

  const onlineOrderFulfillmentId =
    nddQuantity && nddQuantity > 0 ? FULFILLMENT_METHODS.NEXTDAY : FULFILLMENT_METHODS.ONLINEORDER;

  const partsInfo = acesEnabled
    ? {
        partGroupId: skuDetails?.attributesInfoVO?.partGroupId,
        partTerminologyId: skuDetails?.attributesInfoVO?.partTerminologyId,
      }
    : {
        originalPartTypeId: skuDetails?.attributesInfoVO?.originalPartTypeId ?? 0,
        productRepositoryId: skuDetails?.attributesInfoVO?.productRepositoryId,
      };

  const addToCartInfo = {
    skuId: skuDetails?.skuId,
    productId: skuDetails?.attributesInfoVO?.productId ?? String(productId),
    vehicleId: skuDetails?.catalogVehicleId ? skuDetails.catalogVehicleId : '',
    fulFillmentId: thresholdHeaderText ? onlineOrderFulfillmentId : fulFillmentId,
    brandName: skuDetails?.attributesInfoVO?.brandName,
    productPartNumber: skuDetails?.attributesInfoVO?.productPartNumber,
    ...partsInfo,
  };

  return skuDetails ? addToCartInfo : {};
};
